import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Avatar,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  Refresh,
  ContentCopy,
  Link,
  Add,
  Image,
} from '@mui/icons-material';
import { allegroApiService } from '../shared/services/allegroApi';
import { compressImage, getFileSizeInMB, shouldCompressImage } from '../shared/utils/imageCompression';
import { areStringsEqual, findBestMatch, debugNormalization, containsString } from '../shared/utils/stringUtils';
import { imageOptimizer } from '../shared/utils/imageOptimizer';
import { useDebounce } from '../inventory/hooks/useDebounce';

// Funkcja do generowania randomowego UUID
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const ShopOffers = () => {
  const { shopId } = useParams();
  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [linkDialog, setLinkDialog] = useState({ open: false, offer: null });
  const [addProductDialog, setAddProductDialog] = useState({ open: false, offer: null });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [newProduct, setNewProduct] = useState({
    externalId: '',
    purchasePrice: '',
    name: '',
    image: null,
    quantity: 0,
  });

  // Nowe stany dla wyszukiwania produktów lokalnych
  const [localProductsSearch, setLocalProductsSearch] = useState('');
  const [localProducts, setLocalProducts] = useState([]);
  const [filteredLocalProducts, setFilteredLocalProducts] = useState([]);
  const [searchingLocalProducts, setSearchingLocalProducts] = useState(false);
  const [selectedLocalProduct, setSelectedLocalProduct] = useState(null);

  // Debounce dla wyszukiwania produktów lokalnych
  const debouncedLocalProductsSearch = useDebounce(localProductsSearch, 300);

  const loadOffers = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (pagination.page - 1) * pagination.limit;
      
      const offersData = await allegroApiService.getShopOffers(
        shopId,
        statusFilter,
        pagination.limit,
        offset
      );
      
      // Sprawdź strukturę odpowiedzi - Allegro zwraca count i totalCount
      const offers = offersData.offers || offersData;
      const total = offersData.totalCount || offersData.count || offersData.total || offers.length || 0;
      
      setOffers(offers);
      setPagination(prev => ({
        ...prev,
        total: total,
      }));
      setError(null);
    } catch (err) {
      setError('Błąd podczas ładowania ofert');
      console.error('Błąd ładowania ofert:', err);
    } finally {
      setLoading(false);
    }
  }, [shopId, statusFilter, pagination.page, pagination.limit]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Filtrowanie ofert po wyszukiwaniu
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = offers.filter(offer => 
        offer.name && containsString(offer.name, searchTerm)
      );
      setFilteredOffers(filtered);
    } else {
      setFilteredOffers(offers);
    }
  }, [offers, searchTerm]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Można dodać powiadomienie o skopiowaniu
    } catch (err) {
      console.error('Błąd podczas kopiowania:', err);
    }
  };

  const handleLinkOffer = (offer) => {
    setLinkDialog({ open: true, offer });
    // Automatycznie wyszukaj produkty lokalne o podobnej nazwie
    if (offer.name) {
      setLocalProductsSearch(offer.name);
      searchLocalProducts(offer.name);
    }
  };



  const handleLinkConfirm = async () => {
    if (!selectedProduct) return;
    
    try {
      // Użyj externalId z wybranego produktu lokalnego lub z ręcznie wpisanego
      const externalId = selectedLocalProduct?.externalId || selectedProduct.externalId;
      
      await allegroApiService.linkOfferToProduct(
        shopId,
        linkDialog.offer.id,
        externalId
      );
      setLinkDialog({ open: false, offer: null });
      setSelectedProduct(null);
      setSelectedLocalProduct(null);
      setLocalProductsSearch('');
      setFilteredLocalProducts([]);
      // Odśwież listę ofert
      loadOffers();
    } catch (err) {
      console.error('Błąd podczas powiązywania oferty:', err);
    }
  };

  const handleLinkCancel = () => {
    setLinkDialog({ open: false, offer: null });
    setSelectedProduct(null);
    setSelectedLocalProduct(null);
    setLocalProductsSearch('');
    setFilteredLocalProducts([]);
  };

  // Nowa funkcja do wyszukiwania produktów lokalnych
  const searchLocalProducts = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setFilteredLocalProducts([]);
      return;
    }

    try {
      setSearchingLocalProducts(true);
      const allProducts = await allegroApiService.getAllProducts();
      const products = allProducts.products || allProducts || [];
      
      // Filtruj produkty po nazwie używając containsString
      const filtered = products.filter(product => 
        product.name && containsString(product.name, searchTerm)
      );
      
      setLocalProducts(products);
      setFilteredLocalProducts(filtered);
    } catch (error) {
      console.error('Błąd podczas wyszukiwania produktów lokalnych:', error);
      setFilteredLocalProducts([]);
    } finally {
      setSearchingLocalProducts(false);
    }
  };

  // useEffect do obsługi debounced search
  useEffect(() => {
    if (debouncedLocalProductsSearch !== localProductsSearch) {
      searchLocalProducts(debouncedLocalProductsSearch);
    }
  }, [debouncedLocalProductsSearch]);

  // Funkcja do wyboru produktu lokalnego
  const handleSelectLocalProduct = (product) => {
    setSelectedLocalProduct(product);
    setSelectedProduct({ externalId: product.externalId });
    setLocalProductsSearch(product.name);
    setFilteredLocalProducts([]); // Ukryj listę po wyborze
  };

  // Funkcja do czyszczenia wyboru produktu lokalnego
  const handleClearLocalProductSelection = () => {
    setSelectedLocalProduct(null);
    setSelectedProduct(null);
    setLocalProductsSearch('');
    setFilteredLocalProducts([]);
  };

  const handleAddProduct = (offer) => {
    // Inicjalizuj formularz z danymi z oferty
    setNewProduct({
      externalId: generateUUID(),
      purchasePrice: offer.sellingMode?.price?.amount || '',
      name: offer.name,
      image: null,
      quantity: offer.stock?.available || 0,
    });
    
    // Ustaw podgląd obrazu z oferty jeśli istnieje
    if (offer.primaryImage?.url) {
      setImagePreview(offer.primaryImage.url);
    } else {
      setImagePreview(null);
    }
    
    setAddProductDialog({ open: true, offer });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        console.log(`📁 Wybrano plik: ${file.name} (${imageOptimizer.getFileSizeInMB(file)}MB)`);
        
        // Użyj nowego optymalizatora
        const optimizedBase64 = await imageOptimizer.fileToOptimizedBase64(file);
        const byteArray = imageOptimizer.base64ToByteArray(optimizedBase64);
        
        setNewProduct({
          ...newProduct, 
          image: {
            data: Array.from(byteArray),
            contentType: file.type
          }
        });
        
        // Wyświetl podgląd
        const previewUrl = `data:${file.type};base64,${optimizedBase64}`;
        setImagePreview(previewUrl);
        
        console.log(`✅ Obraz przetworzony i zoptymalizowany`);
      } catch (error) {
        console.error('Error processing image:', error);
        alert('Błąd podczas przetwarzania obrazu: ' + error.message);
      }
    }
  };

  // Funkcja do pobierania obrazu z URL i konwersji na byte array z optymalizacją
  const fetchImageAsByteArray = async (url) => {
    try {
      console.log(`🌐 Pobieram obraz z URL: ${url}`);
      
      // Dodaj timeout dla fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 sekund timeout
      
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const arrayBuffer = await res.arrayBuffer();

      // Sprawdź czy arrayBuffer jest poprawny
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('Pusty lub nieprawidłowy ArrayBuffer');
      }

      // Tu miałoby być Twoje "optymalizowanie" – niech zwróci ArrayBuffer
      const optimizedBuffer = await imageOptimizer.optimizeArrayBuffer(arrayBuffer, 0.2); // Zmniejszone do 0.2MB

      console.log(`✅ Obraz zoptymalizowany z URL`);
      return Array.from(new Uint8Array(optimizedBuffer));
    } catch (error) {
      console.error('Błąd podczas pobierania i optymalizacji obrazu:', error);
      return null;
    }
  };

  const handleAddProductCancel = () => {
    setAddProductDialog({ open: false, offer: null });
    setImagePreview(null);
  };

  const handleUpdateLocalProductImage = async (offer) => {
    try {
      // Znajdź produkt lokalny o tej samej nazwie - pobierz WSZYSTKIE produkty lokalne
      const localProducts = await allegroApiService.getAllProducts();
      
      // Debug: wyświetl nazwy produktów lokalnych
      console.log('🔍 Szukam produktu lokalnego dla oferty:', offer.name);
      console.log('📦 Liczba pobranych produktów lokalnych:', localProducts.products?.length || 0);
      console.log('📦 Dostępne produkty lokalne:', localProducts.products?.map(p => p.name));
      
      // Debug normalizacji dla oferty
      debugNormalization(offer.name);
      
      // Użyj localeCompare do porównania stringów
      console.log('🎯 Nazwa oferty:', offer.name);
      
      const matchingProduct = localProducts.products?.find(product => {
        if (!product.name) return false;
        
        console.log(`\n🔍 Sprawdzam produkt lokalny: "${product.name}"`);
        debugNormalization(product.name);
        
        // Użyj localeCompare do porównania
        const isMatch = areStringsEqual(product.name, offer.name);
        console.log(`🔍 Porównuję: "${product.name}" z "${offer.name}" -> ${isMatch ? 'DOPASOWANIE' : 'BRAK DOPASOWANIA'}`);
        
        if (isMatch) {
          console.log('✅ Znaleziono dopasowanie!');
          return true;
        } else {
          console.log('❌ Brak dopasowania');
          return false;
        }
      });

      if (!matchingProduct) {
        console.log('❌ Nie znaleziono dokładnego dopasowania, próbuję częściowe dopasowanie...');
        
        // Spróbuj znaleźć częściowe dopasowanie
        const partialMatch = findBestMatch(offer.name, localProducts.products?.map(p => p.name) || []);
        
        if (partialMatch) {
          console.log('✅ Znaleziono częściowe dopasowanie:', partialMatch);
          const partialMatchingProduct = localProducts.products?.find(p => p.name === partialMatch);
          
          if (partialMatchingProduct) {
            const confirmUpdate = window.confirm(
              `Znaleziono podobny produkt lokalny: "${partialMatchingProduct.name}"\n\n` +
              `Czy chcesz zaktualizować zdjęcie tego produktu?`
            );
            
            if (confirmUpdate) {
              // Kontynuuj z częściowym dopasowaniem
              const matchingProduct = partialMatchingProduct;
            } else {
              return;
            }
          } else {
            alert(`Nie znaleziono produktu lokalnego o nazwie: "${offer.name}"`);
            return;
          }
        } else {
          console.log('❌ Nie znaleziono pasującego produktu lokalnego');
          alert(`Nie znaleziono produktu lokalnego o nazwie: "${offer.name}"`);
          return;
        }
      }
      
      console.log('✅ Znaleziono pasujący produkt lokalny:', matchingProduct.name);

      // Pobierz obraz z oferty
      if (offer.primaryImage?.url) {
        const imageData = await fetchImageAsByteArray(offer.primaryImage.url);
        if (imageData) {
          const imageDto = {
            data: imageData,
            contentType: 'image/jpeg'
          };

          // Dodaj id zdjęcia jeśli istnieje
          if (matchingProduct.imageId) {
            imageDto.id = matchingProduct.imageId;
          }

          // Aktualizuj zdjęcie produktu lokalnego
          await allegroApiService.updateProductImage(matchingProduct.id, imageDto);
          alert('Zdjęcie produktu lokalnego zostało zaktualizowane!');
        } else {
          alert('Nie udało się pobrać obrazu z oferty.');
        }
      } else {
        alert('Oferta nie ma obrazu.');
      }
    } catch (error) {
      console.error('Błąd podczas aktualizacji zdjęcia produktu lokalnego:', error);
      alert('Błąd podczas aktualizacji zdjęcia: ' + error.message);
    }
  };

  const handleAddProductConfirm = async () => {
    if (!addProductDialog.offer || !newProduct.name || !newProduct.purchasePrice) return;
    
    try {
      const offer = addProductDialog.offer;
      
      // 1. Pobierz obraz z oferty jeśli istnieje i nie został zmieniony przez użytkownika
      let finalProduct = { ...newProduct };
      
      if (offer.primaryImage?.url && !newProduct.image) {
        console.log('Pobieranie obrazu z oferty:', offer.primaryImage.url);
        const imageData = await fetchImageAsByteArray(offer.primaryImage.url);
        if (imageData) {
          finalProduct.image = {
            data: imageData,
            contentType: 'image/jpeg'
          };
          console.log('Obraz pobrany z oferty i przekonwertowany na byte array');
        }
      }
      
      console.log('Tworzenie nowego produktu lokalnego z UUID:', finalProduct.externalId);
      console.log('Dane produktu:', finalProduct);
      
      // 2. Dodaj produkt lokalny z danymi z formularza
      const createdProduct = await allegroApiService.addProduct(finalProduct);
      console.log('Utworzony produkt lokalny:', createdProduct);
      
      // 3. Zaktualizuj ofertę, żeby miała external.id = external ID nowego produktu lokalnego
      await allegroApiService.linkOfferToProduct(
        shopId,
        offer.id,
        finalProduct.externalId
      );
      console.log('Oferta zaktualizowana z external.id:', finalProduct.externalId);
      
      setAddProductDialog({ open: false, offer: null });
      setImagePreview(null);
      
      // 4. Odśwież listę ofert aby zobaczyć zmiany
      loadOffers();
      
    } catch (err) {
      console.error('Błąd podczas dodawania produktu:', err);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      ACTIVE: { label: 'Aktywna', color: 'success' },
      INACTIVE: { label: 'Nieaktywna', color: 'default' },
      ENDED: { label: 'Zakończona', color: 'error' },
      SUSPENDED: { label: 'Zawieszona', color: 'warning' },
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          Oferty sklepu (ID: {shopId})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={loadOffers}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
          }}
        >
          Odśwież
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtry i wyszukiwanie */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Wyszukaj oferty..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchTerm && (
                    <Button
                      size="small"
                      onClick={() => setSearchTerm('')}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      Wyczyść
                    </Button>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                >
                  <MenuItem value="ACTIVE">Aktywne</MenuItem>
                  <MenuItem value="INACTIVE">Nieaktywne</MenuItem>
                  <MenuItem value="ENDED">Zakończone</MenuItem>
                  <MenuItem value="SUSPENDED">Zawieszone</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Pokazano: {filteredOffers.length} z {pagination.total} ofert
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pagination.limit}
                    onChange={(e) => {
                      setPagination(prev => ({ ...prev, limit: e.target.value, page: 1 }));
                    }}
                    sx={{ height: 40 }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela ofert */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Nazwa</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Offer ID</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>External ID</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Cena</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Ilość produktów</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: 'background.default' }}>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOffers.map((offer) => (
              <TableRow key={offer.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {offer.primaryImage && (
                      <Avatar
                        src={offer.primaryImage.url}
                        sx={{ mr: 2, width: 40, height: 40, borderRadius: 2 }}
                      />
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {offer.name || 'Brak nazwy'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                      {offer.id}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(offer.id)}
                      sx={{ 
                        ml: 1,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                      {offer.external?.id || 'Brak'}
                    </Typography>
                    {offer.external?.id && (
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(offer.external.id)}
                        sx={{ 
                          ml: 1,
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {getStatusChip(offer.publication?.status || 'UNKNOWN')}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {offer.sellingMode?.price?.amount ? `${offer.sellingMode.price.amount} ${offer.sellingMode.price.currency || 'PLN'}` : 'Brak ceny'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {offer.stock?.available || offer.quantity || 0}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="success"
                    size="small"
                    onClick={() => handleLinkOffer(offer)}
                    title="Powiąż z produktem lokalnym"
                  >
                    <Link />
                  </IconButton>
                  {/* Przycisk dodawania produktu lokalnego - pokazuje się tylko gdy brak external.id */}
                  {!offer.external?.id && (
                    <IconButton
                      color="info"
                      size="small"
                      onClick={() => handleAddProduct(offer)}
                      title="Dodaj jako produkt lokalny"
                      sx={{ ml: 1 }}
                    >
                      <Add />
                    </IconButton>
                  )}
                  {/* Przycisk aktualizacji zdjęcia produktu lokalnego */}
                  <IconButton
                    color="warning"
                    size="small"
                    onClick={() => handleUpdateLocalProductImage(offer)}
                    title="Update zdjęcia produktu lokalnego o tej samej nazwie"
                    sx={{ ml: 1 }}
                  >
                    <Image />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && filteredOffers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm || statusFilter !== 'ACTIVE' 
                      ? 'Nie znaleziono ofert spełniających kryteria wyszukiwania'
                      : 'Brak ofert w tym sklepie'
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginacja */}
      {pagination.total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, p: 2, backgroundColor: 'background.paper', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {searchTerm.trim() ? (
              `Znaleziono: ${filteredOffers.length} ofert`
            ) : (
              `Strona ${pagination.page} z ${Math.ceil(pagination.total / pagination.limit)} (${pagination.total} ofert łącznie)`
            )}
          </Typography>
          {!searchTerm.trim() && (
            <Pagination
              count={Math.ceil(pagination.total / pagination.limit)}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      )}

      {/* Modal powiązania oferty */}
      <Dialog open={linkDialog.open} onClose={handleLinkCancel} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Powiąż ofertę z produktem lokalnym
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Oferta: {linkDialog.offer?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Wyszukaj produkt lokalny po nazwie lub wpisz external ID ręcznie.
            </Typography>
            
            {/* Informacja o funkcjonalności */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> Możesz wyszukać produkt lokalny po nazwie lub wpisać External ID ręcznie. 
                Wybrany produkt lokalny automatycznie przypisze swój External ID do oferty.
              </Typography>
            </Alert>

            {/* Sekcja 1: Wyszukiwanie produktów lokalnych */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Wyszukaj produkt lokalny:
              </Typography>
              <TextField
                fullWidth
                placeholder="Wpisz nazwę produktu lokalnego..."
                value={localProductsSearch}
                onChange={(e) => {
                  const searchTerm = e.target.value;
                  setLocalProductsSearch(searchTerm);
                  // Wyczyść ręcznie wpisany external ID jeśli użytkownik wyszukuje produkty lokalne
                  if (selectedProduct && !selectedLocalProduct) {
                    setSelectedProduct(null);
                  }
                }}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {searchingLocalProducts && <CircularProgress size={20} sx={{ mr: 1 }} />}
                      {selectedLocalProduct && (
                        <Button
                          size="small"
                          onClick={handleClearLocalProductSelection}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          Wyczyść
                        </Button>
                      )}
                    </Box>
                  ),
                }}
              />
              
              {/* Lista wyników wyszukiwania */}
              {filteredLocalProducts.length > 0 && (
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {filteredLocalProducts.map((product) => (
                    <Box
                      key={product.id}
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:hover': { backgroundColor: 'action.hover' },
                        backgroundColor: selectedLocalProduct?.id === product.id ? 'action.selected' : 'transparent',
                      }}
                      onClick={() => handleSelectLocalProduct(product)}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {product.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        External ID: {product.externalId} | Ilość: {product.quantity || 0}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              {/* Komunikat gdy nie znaleziono produktów */}
              {localProductsSearch.length >= 2 && !searchingLocalProducts && filteredLocalProducts.length === 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Nie znaleziono produktów lokalnych o nazwie "{localProductsSearch}"
                </Alert>
              )}
              
              {/* Komunikat gdy wyszukiwanie trwa */}
              {searchingLocalProducts && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Wyszukiwanie produktów...
                </Alert>
              )}
              
              {/* Informacja o wybranym produkcie */}
              {selectedLocalProduct && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Wybrany produkt: <strong>{selectedLocalProduct.name}</strong> (ID: {selectedLocalProduct.externalId})
                </Alert>
              )}
            </Box>

            {/* Sekcja 2: Ręczne wpisanie External ID */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Lub wpisz External ID ręcznie:
              </Typography>
              <TextField
                fullWidth
                placeholder="Wpisz External ID produktu..."
                value={selectedProduct?.externalId || ''}
                onChange={(e) => {
                  const externalId = e.target.value.trim();
                  if (externalId) {
                    setSelectedProduct({ externalId });
                    // Wyczyść wybór produktu lokalnego jeśli wpisano ręcznie
                    if (selectedLocalProduct) {
                      setSelectedLocalProduct(null);
                      setLocalProductsSearch('');
                      setFilteredLocalProducts([]);
                    }
                  } else {
                    setSelectedProduct(null);
                  }
                }}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: selectedProduct?.externalId && !selectedLocalProduct && (
                    <Button
                      size="small"
                      onClick={() => setSelectedProduct(null)}
                      sx={{ minWidth: 'auto', px: 1 }}
                    >
                      Wyczyść
                    </Button>
                  ),
                }}
              />
              {selectedProduct?.externalId && !selectedLocalProduct && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Użyjesz External ID: <strong>{selectedProduct.externalId}</strong>
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLinkCancel}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkConfirm}
            disabled={!selectedProduct || !selectedProduct.externalId}
          >
            Powiąż ofertę
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal dodawania produktu lokalnego */}
      <Dialog open={addProductDialog.open} onClose={handleAddProductCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Dodaj jako produkt lokalny</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="External ID"
              value={newProduct.externalId}
              disabled
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <Button
                    size="small"
                    onClick={() => setNewProduct({...newProduct, externalId: generateUUID()})}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Regeneruj
                  </Button>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Purchase Price"
              value={newProduct.purchasePrice}
              onChange={(e) => setNewProduct({...newProduct, purchasePrice: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Nazwa produktu"
              value={newProduct.name}
              onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Ilość"
              type="number"
              value={newProduct.quantity}
              onChange={(e) => setNewProduct({...newProduct, quantity: parseInt(e.target.value) || 0})}
              sx={{ mb: 2 }}
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<Image />}
              sx={{ mb: 2 }}
            >
              Dodaj zdjęcie
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
            {addProductDialog.offer?.primaryImage?.url && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                💡 Obraz zostanie automatycznie pobrany z oferty jeśli nie wybierzesz własnego
              </Typography>
            )}
            {imagePreview && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Podgląd zdjęcia:</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '150px', 
                      borderRadius: 8,
                      objectFit: 'contain',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }} 
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddProductCancel}>
            Anuluj
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddProductConfirm}
            disabled={!newProduct.purchasePrice || !newProduct.name}
            color="success"
          >
            Dodaj produkt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopOffers; 